import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { Video } from '../../../../utils/api/videoApi';
import { getStreamingSrc, VIDEO_ORIENTATIONS } from '../../../../utils/helpers/video';
import { breakpoints, Color, colors, spacing } from '../../../../utils/styleguide';
import ButtonPromptWrapper from '../../ButtonPromptWrapper';
import PrimaryButton, { ButtonColor } from '../../buttons/PrimaryButton';
import { ImageStyleProps } from '../../../contentful-elements/Image';
import Headliner from '../../Headliner';
import BackgroundVideo from '../../page-sections/BackgroundVideo';
import PropsComponent from '../../types/PropsComponent';
import { Variant } from '../../types/VariantTypes';
import { Col, Row } from '../12ColGridLayout';
import { Logo, Logos } from './Logos';
import { ScrollHintArrow } from './ScrollHintArrow';

const HeadlinerButton = styled(PrimaryButton)`
  border-radius: ${spacing[7]}px;
`;

const Content = styled.div`
  height: 86vh;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto 0;
  ${breakpoints.tablet} {
    padding: ${spacing[11]}px ${spacing[4]}px;
  }
  ${breakpoints.desktop} {
    padding: 140px 0;
    height: 99vh;
  }
`;
const TagWrapper = styled.div`
  margin-bottom: ${spacing[4]}px;
`;

const TitleWrapper = styled.div``;

const DescriptionWrapper = styled.div`
  margin-top: ${spacing[4]}px;
  ${breakpoints.desktop} {
    margin-top: ${spacing[6]}px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-top: ${spacing[6]}px;
`;

type HeroStandardLayoutProps = {
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  image?: PropsComponent<ImageStyleProps>;
  CTAColor?: ButtonColor;
  CTAVariant?: 'solid' | 'outline';
  CTAText?: ReactNode;
  CTAPrompt?: string;
  href?: string;
  backgroundColor?: Color;
  backgroundLinear?: string;
  backgroundBlend?: string;
  landscapeBackgroundVideo?: Video;
  portraitBackgroundVideo?: Video;
  logos?: Logo[];
  variant?: Variant;
  tag?: ReactNode;
};

const HeroStandardLayout = ({
  id,
  title,
  description,
  image,
  CTAText,
  CTAPrompt,
  CTAColor,
  CTAVariant,
  href,
  backgroundColor,
  backgroundLinear,
  landscapeBackgroundVideo,
  portraitBackgroundVideo,
  logos,
  variant,
  tag,
}: HeroStandardLayoutProps) => {
  const content = (
    <Content>
      <Row>
        {tag && (
          <Col tablet={'1 / span 8'} desktop={'1 / span 10'}>
            <TagWrapper>{tag}</TagWrapper>
          </Col>
        )}
        {title && (
          <Col tablet={'1 / span 8'} desktop={'1 / span 10'}>
            <TitleWrapper>{title}</TitleWrapper>
          </Col>
        )}
        {description && (
          <Col tablet={'1 / span 6'}>
            <DescriptionWrapper>{description}</DescriptionWrapper>
          </Col>
        )}
        {logos && (
          <Col tablet={'1 / span 6'}>
            <Logos logos={logos} variant={variant} />
          </Col>
        )}
        {CTAText && (
          <Col tablet={'1 / span 6'}>
            <ButtonPromptWrapper prompt={CTAPrompt}>
              <ButtonWrapper>
                <HeadlinerButton href={href} color={CTAColor} variant={CTAVariant}>
                  {CTAText || 'Learn more'}
                </HeadlinerButton>
              </ButtonWrapper>
            </ButtonPromptWrapper>
          </Col>
        )}
      </Row>
      <ScrollHintArrow style={{ position: 'absolute', bottom: `${spacing[3]}px` }} />
    </Content>
  );

  return (
    <Headliner
      id={id}
      backgroundColor={
        !image
          ? backgroundColor && colors[backgroundColor]
          : backgroundColor ?? 'rgba(0, 0, 0, 0.3)'
      }
      backgroundLinear={backgroundLinear}
      Image={image}
    >
      {landscapeBackgroundVideo && portraitBackgroundVideo ? (
        <BackgroundVideo
          videoLargeScreenUrl={getStreamingSrc({
            video: landscapeBackgroundVideo,
            orientation: VIDEO_ORIENTATIONS.LANDSCAPE,
          })}
          videoSmallScreenUrl={getStreamingSrc({
            video: portraitBackgroundVideo,
            orientation: VIDEO_ORIENTATIONS.PORTRAIT,
          })}
          posterLargeScreenUrl={landscapeBackgroundVideo.metaData.posterSrc}
          posterSmallScreenUrl={portraitBackgroundVideo.metaData.posterSrc}
          showControls={false}
          hasOverlay
        >
          {content}
        </BackgroundVideo>
      ) : (
        <>{content}</>
      )}
    </Headliner>
  );
};

export default HeroStandardLayout;
